import {
  Album,
  AlbumDetail,
  CategoriesResponse,
  GalleryPhoto,
  ItemResponse,
  ListResponse,
  Meta,
  NewsArticle,
} from './contentTypes';

/**
 * Client for the MyBank Content API served by the ERP.
 * Contract: docs/content-api/README.md
 *
 * Note: this is a Create React App project, so the base URL comes from
 * process.env.REACT_APP_* (inlined at build time) — not import.meta.env.
 */
export const CONTENT_API_BASE =
  process.env.REACT_APP_CONTENT_API?.replace(/\/$/, '') || 'https://core.mybank.so/mybank/api/v1/';

/** Set REACT_APP_CONTENT_API_ENABLED=false to serve the bundled static content instead. */
export const CONTENT_API_ENABLED = process.env.REACT_APP_CONTENT_API_ENABLED !== 'false';

const REQUEST_TIMEOUT_MS = 8000;

export class ContentApiError extends Error {
  constructor(public status: number, public code: string, message: string) {
    super(message);
    this.name = 'ContentApiError';
  }
}

/** Accepts plain param objects as well as the typed interfaces below. */
async function get<T>(path: string, params: object = {}): Promise<T> {
  const url = new URL(CONTENT_API_BASE + path);
  Object.entries(params as Record<string, string | number | undefined>).forEach(([key, value]) => {
    if (value !== undefined && value !== '') url.searchParams.set(key, String(value));
  });

  // The newsroom blocks first paint on this call — never let it hang forever.
  const abort = new AbortController();
  const timer = setTimeout(() => abort.abort(), REQUEST_TIMEOUT_MS);

  try {
    const response = await fetch(url.toString(), {
      signal: abort.signal,
      headers: { Accept: 'application/json' },
    });

    if (!response.ok) {
      const body = await response.json().catch(() => null);
      throw new ContentApiError(
        response.status,
        body?.error?.code ?? 'http_error',
        body?.error?.message ?? response.statusText
      );
    }

    return (await response.json()) as T;
  } catch (error) {
    if (error instanceof ContentApiError) throw error;
    if (error instanceof DOMException && error.name === 'AbortError') {
      throw new ContentApiError(0, 'timeout', 'The content service took too long to respond.');
    }
    throw new ContentApiError(0, 'network_error', 'Could not reach the content service.');
  } finally {
    clearTimeout(timer);
  }
}

export interface NewsListParams {
  page?: number;
  per_page?: number;
  category?: string;
  type?: 'internal' | 'external';
  search?: string;
}

export const contentApi = {
  news: (params: NewsListParams = {}) => get<ListResponse<NewsArticle>>('/news', params),

  article: (slugOrId: string) =>
    get<ItemResponse<NewsArticle>>(`/news/${encodeURIComponent(slugOrId)}`),

  albums: (params: { page?: number; per_page?: number } = {}) =>
    get<ListResponse<Album>>('/albums', params),

  album: (slugOrId: string, params: { page?: number; per_page?: number } = {}) =>
    get<ItemResponse<AlbumDetail> & { meta: Meta }>(
      `/albums/${encodeURIComponent(slugOrId)}`,
      params
    ),

  photos: (album: string, page = 1) =>
    get<ListResponse<GalleryPhoto>>('/gallery', { album, page }),

  galleryByCategory: (params: { category?: string; page?: number; per_page?: number } = {}) =>
    get<ListResponse<GalleryPhoto>>('/gallery', params),

  categories: () => get<ItemResponse<CategoriesResponse>>('/categories'),
};
